import Section from "@/Components/MagicLink/Section";
import { usePublicRequestProvider } from "@/Provider/PublicRequestProvider";
import {
    TextField,
    useTheme,
    IconButton,
    Popover,
    Box,
} from "@mui/material";
import { DatepickerCalendarIcon, SearchIcon } from "request-form-lib";
import { Controller } from "react-hook-form";
import { useState } from "react";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";

const FORMAT = "DD.MM.YYYY";

export const ContractWithdrawalForm = () => {
    const theme = useTheme();

    const {
        findContractForm: { control, getErrorPropsForField },
    } = usePublicRequestProvider();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const open = Boolean(anchorEl);

    const calendarRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                const el = calendarRef.current?.querySelector(
                    '[role="gridcell"][tabindex="0"]'
                ) as HTMLElement | null;

                el?.focus();
            });
        }
    }, [open]);

    const parseSafeDate = (value: string | undefined) => {
        if (!value) return null;

        const parsed = dayjs(value, FORMAT, true);
        return parsed.isValid() ? parsed : null;
    };

    return (
        <Section
            icon={
                <SearchIcon
                    width={28}
                    height={28}
                    color={theme.palette.primary.main}
                />
            }
            title="Identifikácia návrhu alebo poistnej zmluvy"
        >
            {/* CONTRACT ID */}
            <Controller
                name="contractOrProposalId"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        id="contractOrProposalId"
                        label="Číslo návrhu alebo poistnej zmluvy"
                        {...getErrorPropsForField("contractOrProposalId")}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: 0,
                                paddingLeft: 0,
                            },
                        }}
                    />
                )}
            />

            {/* EMAIL */}
            <Controller
                name="email"
                control={control}
                render={({ field }) => {
                    const emailFieldProps =
                        getErrorPropsForField("email");

                    return (
                        <TextField
                            {...field}
                            fullWidth
                            id="email"
                            type="email"
                            label="Emailová adresa"
                            autoComplete="email"
                            {...emailFieldProps}
                            helperText={
                                emailFieldProps.helperText ??
                                "Zadajte e-mail, ktorý ste uviedli pri uzatvorení zmluvy."
                            }
                            FormHelperTextProps={{
                                sx: {
                                    marginLeft: 0,
                                    paddingLeft: 0,
                                },
                            }}
                        />
                    );
                }}
            />

            {/* BIRTH DATE (FIXED) */}
            <Controller
                name="birthDate"
                control={control}
                render={({ field }) => {
                    return (
                        <>
                            <TextField
                                {...field}
                                value={field.value || ""}
                                fullWidth
                                id="birthDate"
                                label="Dátum narodenia poistníka"
                                placeholder="DD.MM.YYYY"
                                {...getErrorPropsForField("birthDate")}
                                FormHelperTextProps={{
                                    sx: {
                                        marginLeft: 0,
                                        paddingLeft: 0,
                                    },
                                }}
                                onChange={(e) =>
                                    field.onChange(e.target.value)
                                }
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            aria-label="Otvoriť výber dátumu"
                                            onClick={(e) =>
                                                setAnchorEl(
                                                    e.currentTarget
                                                )
                                            }
                                            edge="end"
                                            sx={{ mr: 0.25 }}
                                        >
                                            <DatepickerCalendarIcon
                                                width={16}
                                                height={16}
                                                color="#000"
                                            />
                                        </IconButton>
                                    ),
                                }}
                            />

                            <Popover
                                open={open}
                                anchorEl={anchorEl}
                                onClose={() =>
                                    setAnchorEl(null)
                                }
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                            >
                                <Box sx={{ p: 1 }}>
                                    <DateCalendar
                                        ref={calendarRef}
                                        value={parseSafeDate(field.value)}
                                        onChange={(val) => {
                                            const formatted =
                                                val?.format(FORMAT) ||
                                                "";

                                            field.onChange(formatted);
                                            setAnchorEl(null);
                                        }}
                                    />
                                </Box>
                            </Popover>
                        </>
                    );
                }}
            />
        </Section>
    );
};